import { QuoteState, useInboxInsuredQuery } from "@qw/graphql";
import { useInsuredActiveOpportunities, useInsuredActivePolicies, useInsuredQuotes } from "@qw/insured";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useInboxQueryStates } from "./inbox-query-states-provider";
import { useConversation } from "./use-conversation";
import { InboxInsuredContext, type InboxInsuredContextValue } from "./use-inbox-insured";
import { usePage } from "./use-page";

export const InsuredProvider = ({ children }: React.PropsWithChildren) => {
  const { inboxConversation, loading: conversationLoading } = useConversation();
  const { conversationId, setConversationId } = useInboxQueryStates();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { insuredId: insuredIdParam } = useParams();
  const { rootPath } = usePage();

  const isInboxPath = pathname === rootPath || pathname.startsWith(`${rootPath}/`);
  const rootSuffix = isInboxPath ? (pathname === rootPath ? "" : pathname.slice(rootPath.length)) : null;
  const tabOnlyRouteSuffix = rootSuffix === "/files" || rootSuffix?.startsWith("/files/") ? rootSuffix : null;
  const routeInsuredId = tabOnlyRouteSuffix ? undefined : insuredIdParam;
  const canUseSelectedConversation = isInboxPath && conversationId;
  const selectedConversation =
    canUseSelectedConversation && inboxConversation?.id === conversationId ? inboxConversation : undefined;
  const selectedConversationPending = Boolean(
    canUseSelectedConversation && conversationLoading && (!inboxConversation || inboxConversation.id !== conversationId)
  );
  const insuredId = selectedConversation
    ? selectedConversation.insuredId
    : selectedConversationPending
      ? undefined
      : routeInsuredId;

  useEffect(() => {
    if (conversationId && !conversationLoading && inboxConversation === null) {
      setConversationId(null, { history: "replace" });
    }
  }, [conversationId, conversationLoading, inboxConversation, setConversationId]);

  useEffect(() => {
    if (!isInboxPath || !conversationId || !selectedConversation) {
      return;
    }

    if (!routeInsuredId) {
      return;
    }

    const routeInsuredPath = `${rootPath}/${routeInsuredId}`;
    const routeSuffix =
      pathname === routeInsuredPath
        ? ""
        : pathname.startsWith(`${routeInsuredPath}/`)
          ? pathname.slice(routeInsuredPath.length)
          : null;

    if (routeSuffix === null) {
      return;
    }

    const nextPathname = selectedConversation.insuredId
      ? `${rootPath}/${selectedConversation.insuredId}${routeSuffix}`
      : rootPath;

    if (pathname !== nextPathname) {
      navigate({ pathname: nextPathname, search }, { replace: true });
    }
  }, [conversationId, isInboxPath, navigate, pathname, rootPath, routeInsuredId, search, selectedConversation]);

  const { data, loading: insuredLoading } = useInboxInsuredQuery({
    variables: { id: insuredId || "" },
    skip: !insuredId,
    fetchPolicy: "cache-and-network",
  });

  const {
    opportunities,
    activeOpportunities,
    refetch: refetchOpportunities,
  } = useInsuredActiveOpportunities(insuredId);
  const { quotes: quotesData } = useInsuredQuotes(insuredId);
  const { activePolicies } = useInsuredActivePolicies(insuredId);

  const draftQuotes = useMemo(
    () =>
      quotesData.filter(
        (quote) =>
          quote.state === QuoteState.Created ||
          quote.state === QuoteState.Unredacted ||
          quote.state === QuoteState.Redacted ||
          quote.state === QuoteState.Processed
      ),
    [quotesData]
  );

  const quotes = useMemo(
    () =>
      quotesData.filter(
        (quote) => quote.state === QuoteState.Delivered || quote.state === QuoteState.BindRequestReceived
      ),
    [quotesData]
  );

  const insured =
    insuredId && data?.insured?.id === insuredId
      ? data.insured
      : insuredId && !data && selectedConversation?.insured?.id === insuredId
        ? selectedConversation.insured
        : undefined;

  const value: InboxInsuredContextValue = {
    activeOpportunities,
    activePolicies,
    draftQuotes,
    insured,
    insuredId,
    loading: selectedConversationPending || insuredLoading,
    opportunities,
    quotes,
    refetchOpportunities,
  };

  return <InboxInsuredContext.Provider value={value}>{children}</InboxInsuredContext.Provider>;
};
